import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  IconButton,
  InputAdornment,
  alpha,
  useTheme,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AnimateButton from '@/components/ui/AnimateButton';
import { loginWithPasswordSchema, type LoginWithPasswordValues } from './zod';

interface PasswordLoginProps {
  onSubmit: (values: LoginWithPasswordValues) => void;
  onForgotPassword: () => void;
  error: string;
  loading: boolean;
}

export default function PasswordLogin({ onSubmit, onForgotPassword, error, loading }: PasswordLoginProps) {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginWithPasswordValues>({
    resolver: zodResolver(loginWithPasswordSchema),
    defaultValues: { password: '' },
    mode: 'onBlur',
  });

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '16px',
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
        }}
      >
        <LockOutlinedIcon sx={{ color: 'primary.main', fontSize: 28 }} />
      </Box>

      <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
        Sign in
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Enter your password to continue.
      </Typography>

      <Stack spacing={2.5}>

        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              id="password-input"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              autoComplete="current-password"
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Box sx={{ textAlign: 'right' }}>
          <Typography
            id="forgot-password-link"
            component="button"
            type="button"
            onClick={onForgotPassword}
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              textDecoration: 'none',
              background: 'none',
              border: 'none',
              p: 0,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Forgot password?
          </Typography>
        </Box>

        {error && (
          <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'center' }}>
            {error}
          </Typography>
        )}

        <AnimateButton>
          <Button
            id="login-with-password-btn"
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ py: 1.5, fontSize: '0.9375rem', borderRadius: '10px' }}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>
        </AnimateButton>
      </Stack>
    </Box>
  );
}
